/**
 * The audio output graph for watch mode (audio.ts decides *what* plays and
 * when; this decides *how* it reaches the speaker).
 *
 * WebAudio first, because it is the only thing that works the same on every
 * browser: one `AudioContext`, created or resumed inside the viewer's own
 * press (Watch, or the sound control), with a gain node per bus — voice,
 * music, sfx — into a master gain (the viewer's volume) and a gentle
 * compressor that only catches peaks. `HTMLMediaElement.volume` is read-only
 * on iOS, so ducking and fading an `<audio>` element there silently does
 * nothing; a `GainNode` ramp works everywhere, and runs on the audio thread,
 * so a fade still finishes in a background tab where `requestAnimationFrame`
 * has stopped.
 *
 * Clips are fetched once and decoded to buffers (cached per URL for this page
 * load only — nothing persisted, CLAUDE.md rule 3), so a clip starts on the
 * sample it is asked to rather than whenever an element finishes buffering,
 * and the looping music bed loops without a gap.
 *
 * Fallback: no `AudioContext` at all, or a clip that cannot be fetched or
 * decoded (a cross-origin asset host without CORS, an old codec), plays
 * through a plain `<audio>` element whose volume is stepped towards the bus
 * level instead. Worse on iOS, identical elsewhere, never silent by design.
 */

export type Bus = 'voice' | 'music' | 'sfx' | 'master';

export interface Clip {
  /** Stop now. Idempotent; fires `onEnd` if it has not fired yet. */
  stop(): void;
  /** Seconds into the source playback has reached — to resume a loop there. */
  position(): number;
}

export interface PlayOptions {
  loop?: boolean;
  /** Seconds into the source to start from. */
  offset?: number;
  /** Sound has actually started (not merely been asked for). */
  onStart?(): void;
  /** Finished, failed, or stopped — exactly once. */
  onEnd?(): void;
}

export interface Mixer {
  /** Call from inside a user gesture: creates/resumes the context (iOS). */
  unlock(): void;
  /** Ramp a bus's gain to `to` over `ms` (0 = at once). */
  ramp(bus: Bus, to: number, ms: number): void;
  play(bus: Exclude<Bus, 'master'>, url: string, opts?: PlayOptions): Clip;
  /** Fetch and decode ahead of need. Never throws. */
  preload(url: string): void;
  destroy(): void;
}

type AnyCtor = typeof AudioContext;

function contextCtor(): AnyCtor | undefined {
  const g = globalThis as unknown as { AudioContext?: AnyCtor; webkitAudioContext?: AnyCtor };
  return g.AudioContext ?? g.webkitAudioContext;
}

interface Live {
  bus: Exclude<Bus, 'master'>;
  el: HTMLAudioElement;
}

const BUSES: Bus[] = ['voice', 'music', 'sfx'];
/** Element fallback: volume steps this often while ramping. */
const STEP_MS = 40;
/**
 * A clip that is stopped (Back, Pause, mute, a tap that takes over) fades to
 * silence over this long rather than being cut at full level, which clicks.
 * Short enough to read as "stopped with the picture".
 */
export const CLIP_STOP_FADE_MS = 40;

export class WebMixer implements Mixer {
  private ctx: AudioContext | null = null;
  /** No AudioContext here, or creating one threw — elements only. */
  private dead = false;
  private readonly nodes = new Map<Bus, GainNode>();
  /** Target level per bus. */
  private readonly level: Record<Bus, number> = { master: 1, voice: 1, music: 1, sfx: 1 };
  /** The element fallback's current (stepped) level per bus. */
  private readonly now: Record<Bus, number> = { master: 1, voice: 1, music: 1, sfx: 1 };
  private readonly steps = new Map<Bus, ReturnType<typeof setInterval>>();
  private readonly buffers = new Map<string, Promise<AudioBuffer>>();
  private readonly els = new Map<string, HTMLAudioElement>();
  private readonly live = new Set<Live>();
  private readonly clips = new Set<Clip>();
  /**
   * Element fallback: bumped whenever an element is (re)started or begins a
   * stop fade, so a fade's pending pause never stops the same element after
   * it has been started again (one element per URL is reused: Back, then
   * Watch, replays the very clip that is still fading out).
   */
  private readonly elGen = new WeakMap<HTMLAudioElement, number>();

  private context(): AudioContext | null {
    if (this.ctx || this.dead) return this.ctx;
    const Ctor = contextCtor();
    if (!Ctor) {
      this.dead = true;
      return null;
    }
    try {
      const c = new Ctor();
      const comp = c.createDynamicsCompressor();
      // A safety net for the rare moment voice, music and an effect peak
      // together — not a leveller: the threshold sits well above where a
      // normalised voice (-16 LUFS) spends its time, so nothing pumps.
      comp.threshold.value = -6;
      comp.knee.value = 6;
      comp.ratio.value = 8;
      comp.attack.value = 0.003;
      comp.release.value = 0.25;
      comp.connect(c.destination);
      const master = c.createGain();
      master.connect(comp);
      master.gain.value = this.level.master;
      this.nodes.set('master', master);
      for (const b of BUSES) {
        const g = c.createGain();
        g.gain.value = this.level[b];
        g.connect(master);
        this.nodes.set(b, g);
      }
      this.ctx = c;
    } catch {
      this.dead = true;
    }
    return this.ctx;
  }

  unlock(): void {
    // Safari 16.4+: play through the ringer switch, like a video would —
    // WebAudio is otherwise "ambient" on iOS and silenced by it.
    try {
      const s = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
      if (s) s.type = 'playback';
    } catch {
      /* not supported — nothing to do */
    }
    const c = this.context();
    if (!c) return;
    if (c.state !== 'running') void c.resume().catch(() => {});
    // Older iOS only unlocks output once something is started inside the
    // gesture itself: one silent sample.
    try {
      const s = c.createBufferSource();
      s.buffer = c.createBuffer(1, 1, 22050);
      s.connect(c.destination);
      s.start(0);
    } catch {
      /* harmless */
    }
  }

  ramp(bus: Bus, to: number, ms: number): void {
    this.level[bus] = to;
    const node = this.nodes.get(bus);
    if (node && this.ctx) {
      const g = node.gain;
      const t = this.ctx.currentTime;
      g.cancelScheduledValues(t);
      g.setValueAtTime(g.value, t);
      if (ms > 0) g.linearRampToValueAtTime(to, t + ms / 1000);
      else g.setValueAtTime(to, t);
    }
    this.stepTo(bus, ms);
  }

  /** Element fallback: step `now[bus]` towards `level[bus]` over `ms`. */
  private stepTo(bus: Bus, ms: number): void {
    const prev = this.steps.get(bus);
    if (prev) clearInterval(prev);
    this.steps.delete(bus);
    const from = this.now[bus];
    const to = this.level[bus];
    const n = Math.floor(ms / STEP_MS);
    if (n < 1 || !this.live.size) {
      this.now[bus] = to;
      this.applyVolumes();
      return;
    }
    let i = 0;
    const id = setInterval(() => {
      i++;
      this.now[bus] = i >= n ? to : from + ((to - from) * i) / n;
      this.applyVolumes();
      if (i >= n) {
        clearInterval(id);
        this.steps.delete(bus);
      }
    }, STEP_MS);
    this.steps.set(bus, id);
  }

  private applyVolumes(): void {
    for (const l of this.live) this.setVolume(l);
  }

  private setVolume(l: Live): void {
    const v = this.now[l.bus] * this.now.master;
    try {
      l.el.volume = v < 0 ? 0 : v > 1 ? 1 : v;
    } catch {
      /* read-only (iOS) — plays at full volume */
    }
  }

  private buffer(url: string): Promise<AudioBuffer> {
    let p = this.buffers.get(url);
    if (!p) {
      const c = this.ctx!;
      // `same-origin` (the browser's default): a clip on the page's own host is
      // fetched exactly as its <img> siblings are, with the page's own session
      // (a demo behind a login, an artifact host, an offline copy served from a
      // private page); a clip on a CDN still goes without credentials, so a
      // plain `Access-Control-Allow-Origin: *` keeps working. With `omit` a
      // host that needs the session answered with its sign-in or not-found
      // page, the decode failed, and the element fallback below took over —
      // which iOS only allows within a few seconds of a tap, so the first
      // narration clip played and every later one was refused (2026-10-12).
      p = fetch(url, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(`${r.status}`);
          const type = r.headers?.get('content-type') ?? '';
          // A page where a clip should be (a sign-in wall, a not-found shell
          // served as 200): fail now, not in the decoder.
          if (/text\/html/i.test(type)) throw new Error('html');
          return r.arrayBuffer();
        })
        // The callback form: Safari before 14.1 has no promise overload.
        .then((b) => new Promise<AudioBuffer>((res, rej) => void c.decodeAudioData(b, res, rej)));
      p.catch(() => {});
      this.buffers.set(url, p);
    }
    return p;
  }

  private bumpGen(el: HTMLAudioElement): number {
    const g = (this.elGen.get(el) ?? 0) + 1;
    this.elGen.set(el, g);
    return g;
  }

  /** Element fallback: step the volume to 0 over `CLIP_STOP_FADE_MS`, then pause. */
  private fadeElement(el: HTMLAudioElement): void {
    const gen = this.bumpGen(el);
    let from = 1;
    try {
      from = el.volume;
    } catch {
      /* unreadable — pause below anyway */
    }
    const n = 4;
    let i = 0;
    const step = (): void => {
      if (this.elGen.get(el) !== gen) return;
      i++;
      try {
        el.volume = Math.max(0, from * (1 - i / n));
      } catch {
        /* read-only (iOS): the pause below is the stop */
      }
      if (i >= n) el.pause();
      else setTimeout(step, CLIP_STOP_FADE_MS / n);
    };
    setTimeout(step, CLIP_STOP_FADE_MS / n);
  }

  private element(url: string): HTMLAudioElement {
    let el = this.els.get(url);
    if (!el) {
      el = new Audio(url);
      this.els.set(url, el);
    }
    return el;
  }

  preload(url: string): void {
    if (this.context()) void this.buffer(url).catch(() => {});
  }

  play(bus: Exclude<Bus, 'master'>, url: string, opts: PlayOptions = {}): Clip {
    const c = this.context();
    let stopped = false;
    let started = false;
    let ended = false;
    let src: AudioBufferSourceNode | null = null;
    /** The clip's own gain, between it and its bus: what a stop fades. */
    let fader: GainNode | null = null;
    let live: Live | null = null;
    let startedAt = 0;
    let duration = 0;
    const offset = opts.offset ?? 0;

    const start = (): void => {
      if (started || stopped) return;
      started = true;
      opts.onStart?.();
    };
    const end = (): void => {
      if (ended) return;
      ended = true;
      if (live) this.live.delete(live);
      this.clips.delete(clip);
      opts.onEnd?.();
    };

    const viaElement = (): void => {
      if (stopped) return;
      const el = this.element(url);
      el.loop = !!opts.loop;
      try {
        el.currentTime = offset;
      } catch {
        /* not seekable yet — plays from the top */
      }
      // A stop fade still running on this element (the same clip, asked for
      // again at once) must not pause it now.
      this.bumpGen(el);
      live = { bus, el };
      this.live.add(live);
      this.setVolume(live);
      // One element per URL, reused: handlers are replaced, not stacked.
      el.onplaying = start;
      el.onended = () => {
        if (!el.loop) end();
      };
      void Promise.resolve(el.play()).then(start, end);
    };

    const clip: Clip = {
      stop: () => {
        if (stopped) return;
        stopped = true;
        // Faded, not cut: a source stopped at full level clicks. Each clip
        // has its own gain, so the fade never touches the next clip on the
        // bus, which may start this very instant.
        if (src && c) {
          const at = c.currentTime + CLIP_STOP_FADE_MS / 1000;
          try {
            if (fader) {
              const g = fader.gain;
              g.cancelScheduledValues(c.currentTime);
              g.setValueAtTime(g.value, c.currentTime);
              g.linearRampToValueAtTime(0, at);
            }
            src.stop(fader ? at : 0);
          } catch {
            /* already stopped */
          }
        }
        if (live) {
          // Out of the bus's volume steps first: they would raise it again.
          this.live.delete(live);
          this.fadeElement(live.el);
        }
        end();
      },
      position: () => {
        if (src && c && duration) return (offset + c.currentTime - startedAt) % duration;
        if (live) return live.el.currentTime || 0;
        return offset;
      },
    };
    this.clips.add(clip);

    if (!c) {
      viaElement();
      return clip;
    }
    if (c.state !== 'running') void c.resume().catch(() => {});
    this.buffer(url).then((buf) => {
      if (stopped) return;
      const node = c.createBufferSource();
      node.buffer = buf;
      node.loop = !!opts.loop;
      const busNode = this.nodes.get(bus)!;
      try {
        fader = c.createGain();
        fader.gain.value = 1;
        fader.connect(busNode);
        node.connect(fader);
      } catch {
        fader = null;
        node.connect(busNode);
      }
      node.onended = () => {
        // Played out, or its stop fade is over: the clip's gain goes too.
        try {
          fader?.disconnect();
        } catch {
          /* never connected */
        }
        end();
      };
      duration = buf.duration;
      startedAt = c.currentTime;
      node.start(0, duration ? offset % duration : 0);
      src = node;
      start();
    }, viaElement);
    return clip;
  }

  destroy(): void {
    for (const clip of [...this.clips]) clip.stop();
    for (const id of this.steps.values()) clearInterval(id);
    this.steps.clear();
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.dead = true;
  }
}
